import { Button } from "@/design-system/Button/Button";
import { showModal } from "@/redux/message/messageSlice";
import {
  changeObjectiveBundle,
  getObjectivesSelector,
  isGroupPlayable,
  isInObjectiveBundleSelector,
} from "@/redux/objectives/objectivesSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import type { ObjectiveLiveEvent } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { Ban, Check, Lock, Minus, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

export const AddToBundleButton = ({
  groupId,
  matchModes,
  liveEvents,
  stretch,
  completed,
  locked,
}: {
  groupId: number;
  /** The group's and the active live events: whether Customize's game modes can play it. */
  matchModes: string[][];
  liveEvents: ObjectiveLiveEvent[];
  stretch?: boolean;
  /** Done in EA: a disabled Completed instead of Add, unless it is in the bundle to remove. */
  completed?: boolean;
  /** Hidden from the user in EA: a disabled Locked, unless it is in the bundle to remove. */
  locked?: boolean;
}) => {
  const { t } = useTranslation("objectives");
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const inBundle = useSelector(isInObjectiveBundleSelector(groupId));
  const { build, bundle } = useSelector(getObjectivesSelector);
  // A change posts the whole bundle, so it waits until the saved one is loaded.
  const { seeded } = build;
  // Not playable in the modes Customize allows; one already in the bundle can still be removed.
  const notPossible =
    !inBundle && !isGroupPlayable(matchModes, bundle.excludedModes, liveEvents);

  if (completed && !inBundle)
    return (
      <Button
        variant="tertiary"
        size="medium"
        stretch={stretch}
        icon={<Check />}
        disabled
      >
        {t("bundle.completed")}
      </Button>
    );

  if (locked && !inBundle)
    return (
      <Button
        variant="tertiary"
        size="medium"
        stretch={stretch}
        icon={<Lock />}
        disabled
      >
        {t("bundle.locked")}
      </Button>
    );

  if (notPossible)
    return (
      <Button
        variant="tertiary"
        size="medium"
        stretch={stretch}
        icon={<Ban />}
        disabled
      >
        {t("bundle.notPossible")}
      </Button>
    );

  return (
    <Button
      variant="tertiary"
      size="medium"
      stretch={stretch}
      icon={inBundle ? <Minus /> : <Plus />}
      disabled={isLoggedIn && !seeded}
      onClick={(e) => {
        // Cards are links.
        e.preventDefault();
        e.stopPropagation();
        if (!isLoggedIn) {
          dispatch(showModal({ modalType: "login-required" }));
          return;
        }
        dispatch(changeObjectiveBundle({ type: "toggle", groupId }));
      }}
    >
      {inBundle ? t("bundle.remove") : t("bundle.add")}
    </Button>
  );
};
